import { useCallback, useMemo } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useNavigate } from '@change-corgi/core/react/router';

import type { Comment, Petition } from 'src/app/pages/petitionComment/pageContext';
import { usePetitionCommentPageData } from 'src/app/pages/petitionComment/pageContext';
import { useCurrentUser } from 'src/app/shared/hooks/session';

type Result = ModelHookResult<
	{
		comment: Comment;
		petition: Petition;
		author: string;
		authorUrl?: string;
		location?: string;
		dateRelative: string;
		canReport: boolean;
		canDelete: boolean;
		trackingProperties: Record<string, string | number | boolean>;
	},
	{
		handleDeletedComment: () => void;
	}
>;

// eslint-disable-next-line complexity
export function useCommentFooter(): Result {
	const {
		data: { comment, petition },
	} = usePetitionCommentPageData();
	const { getCountry } = useI18n();
	const currentUser = useCurrentUser();

	const { user, createdAtRelativeLocalized } = comment;
	const navigate = useNavigate();

	return {
		data: {
			comment,
			petition,
			author: user.displayName,
			authorUrl: user.anonymous ? undefined : `/u/${encodeURIComponent(user.slug || user.id)}`,
			location:
				[user.city, user.stateCode, user.country ? getCountry(user.country.countryCode)?.name : undefined]
					.filter((str): str is string => !!str)
					.join(', ') || undefined,
			dateRelative: createdAtRelativeLocalized,
			canReport: currentUser?.id !== user.id,
			canDelete:
				!!currentUser?.roles.support ||
				!!currentUser?.roles.campaigns ||
				currentUser?.id === comment.user.id ||
				currentUser?.id === petition.user.id,
			trackingProperties: useMemo(
				() => ({
					commentable_type: 'Event',
					commentable_id: petition.id,
					own_petition: petition.user.id === currentUser?.id,
				}),
				[petition, currentUser],
			),
		},
		actions: {
			handleDeletedComment: useCallback(() => {
				navigate(`/p/${encodeURIComponent(petition.slug)}/c?deleted_comment=true`);
			}, [petition, navigate]),
		},
	};
}
